import moment from "moment";
import { buildLibraryRequestDetailsPath } from "./libraryRequestsEmailLinkPaths";
import { getListRowLocationDisplay } from "./addressUtils";

export const LIBRARY_REQUEST_ACTIONS = {
  requested: "Requested",
  accepted: "Accepted",
  rejected: "Rejected",
};

/** User-facing label for library-request reports in activity copy (not document sub-type). */
export const LIBRARY_REQUEST_ACTIVITY_REPORT_LABEL = "VQA Report";

const EMAIL_LIKE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function looksLikeEmail(value) {
  const trimmed = String(value ?? "").trim();
  return Boolean(trimmed && EMAIL_LIKE.test(trimmed));
}

export function formatPersonWithEmailInParentheses(displayName, email) {
  const name = String(displayName ?? "").trim();
  const mail = String(email ?? "").trim();
  if (name && mail) return `${name} (${mail})`;
  return mail || name || "";
}

export const resolveLibraryRequestAudience = (role) => {
  const normalized = String(role ?? "").trim().toLowerCase();
  if (normalized.startsWith("sponsor")) return "sponsor";
  if (normalized.startsWith("provider")) return "provider";
  return "admin";
};

export const resolveLibraryRequestDetailsPath = (item) => {
  const type = String(item?.document_type ?? "").toUpperCase();
  if (type === "VQA" && item?.request_id) {
    return buildLibraryRequestDetailsPath(item.request_id);
  }
  if (type === "RFI" && item?.hc_questionnaire_id) {
    return `/RFI/${item.hc_questionnaire_id}/Details`;
  }
  return null;
};

export const formatLibraryRequestDate = (value) =>
  value ? moment.utc(value).format("MMMM D, YYYY") : "-";

/** Activity timeline dates — same shape as VQQ Details (`RFIDetails`). */
export const formatLibraryRequestActivityDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

export const formatLibraryRequestDateTime = (value) =>
  value ? moment.utc(value).local().format("MMMM D, YYYY h:mm A") : "-";

const scopeNames = (capabilities) =>
  (capabilities ?? []).map((capability) => capability?.rfi_name).filter(Boolean);

const firstPresent = (...values) =>
  values.map((value) => String(value ?? "").trim()).find(Boolean) || "";

const activityValue = (activity, action, key) =>
  (activity ?? []).find((item) => item?.action === action)?.[key];

export const resolveLibraryRequestContacts = (details) => ({
  sponsorContact: firstPresent(
    details?.requested_by,
    activityValue(details?.activity, LIBRARY_REQUEST_ACTIONS.requested, "actor")
  ),
  vendorContact: firstPresent(
    details?.vendor_contact,
    activityValue(details?.activity, LIBRARY_REQUEST_ACTIONS.accepted, "actor"),
    activityValue(details?.activity, LIBRARY_REQUEST_ACTIONS.rejected, "actor"),
    activityValue(details?.activity, LIBRARY_REQUEST_ACTIONS.requested, "vendor_contact")
  ),
});

export const buildLibraryRequestOverviewRows = (details, audience, actions = {}) => {
  if (!details) return [];
  const isAdmin = audience === "admin";
  const additionalScope = details.additional_scope ?? [];
  const { sponsorContact, vendorContact } = resolveLibraryRequestContacts(details);

  return [
    { key: "status", label: "Status", type: "status", value: details.request_status },
    {
      key: "report",
      label: "Report Name",
      type: "report",
      value: details.report_type || details.document_name,
      canPreview: Boolean(details.can_preview && actions.onPreview),
      onPreview: actions.onPreview,
    },
    { key: "vendor", label: "Vendor", value: details.provider_name },
    { key: "vendorContact", label: "Vendor Contact", value: vendorContact },
    { key: "sponsor", label: "Sponsor", value: details.sponsor_name },
    { key: "requestedBy", label: "Sponsor Contact", value: sponsorContact },
    { key: "scope", label: "Scope", type: "badges", value: scopeNames(details.capabilities) },
    additionalScope.length > 0
      ? { key: "additionalScope", label: "Additional Scope", type: "badges", value: additionalScope }
      : null,
    {
      key: "location",
      label: "Location",
      // Same full names as the Library Requests grid ("Encamp, Andorra"), not stored codes.
      value: getListRowLocationDisplay(details, { short: false, abbvState: false }),
    },
    { key: "reportDate", label: "Report Date", type: "date", value: details.published_on },
    { key: "requestDate", label: "Request Date", type: "date", value: details.requested_on },
    details.released_on
      ? { key: "acceptedDate", label: "Accepted Date", type: "date", value: details.released_on }
      : null,
    details.is_rejected
      ? { key: "rejectedDate", label: "Rejected Date", type: "date", value: details.rejected_on }
      : null,
    details.is_rejected
      ? { key: "rejectReason", label: "Reject Reason", value: details.rejected_reason }
      : null,
    isAdmin && details.archive_comment
      ? { key: "archiveComment", label: "Archive Comment", value: details.archive_comment }
      : null,
  ].filter(Boolean);
};

export function resolveLibraryRequestActivityPersonName(item) {
  const first = firstPresent(item?.actor_first_name, item?.actorFirstName);
  const last = firstPresent(item?.actor_last_name, item?.actorLastName);
  const fromFields = [first, last].filter(Boolean).join(" ").trim();
  if (fromFields) return fromFields;

  const actor = String(item?.actor ?? "").trim();
  if (actor && !looksLikeEmail(actor)) return actor;
  return "";
}

export function resolveLibraryRequestActivityEmail(item, fallbacks = {}) {
  const explicit = firstPresent(item?.actor_email, item?.actorEmail);
  if (explicit) return explicit;

  const actor = String(item?.actor ?? "").trim();
  if (looksLikeEmail(actor)) return actor;

  // Release/reject entries name only who acted, never today's vendor contact (log is history).
  const isRequest = item?.action === LIBRARY_REQUEST_ACTIONS.requested;
  return isRequest ? firstPresent(fallbacks.sponsorContact) : "";
}

export function formatLibraryRequestActivityActorLabel(item, fallbacks = {}) {
  const email = resolveLibraryRequestActivityEmail(item, fallbacks);
  const name = resolveLibraryRequestActivityPersonName(item);
  return formatPersonWithEmailInParentheses(name, email);
}

/** "at 10:00 AM on September 1, 2026" — local time, for requested activity lines. */
export function formatLibraryRequestActivityAtTimeOnDate(value) {
  if (!value) return "";
  const local = moment.utc(value).local();
  const time = local.format("h:mm A");
  const date = local.format("MMMM D, YYYY");
  return `at ${time} on ${date}`;
}

/** Single-line activity copy aligned with notification / prototype wording (14164). */
export function formatLibraryRequestActivityMessage(item) {
  const vendor = firstPresent(item?.vendor_name, "the vendor");
  const sponsor = firstPresent(item?.sponsor_name, "the sponsor");
  const report = LIBRARY_REQUEST_ACTIVITY_REPORT_LABEL;

  switch (item?.action) {
    case LIBRARY_REQUEST_ACTIONS.requested: {
      const when = formatLibraryRequestActivityAtTimeOnDate(item?.action_on);
      return when
        ? `requested a ${report} from ${vendor} ${when}.`
        : `requested a ${report} from ${vendor}.`;
    }
    case LIBRARY_REQUEST_ACTIONS.accepted:
      return `released ${report} to ${sponsor}.`;
    case LIBRARY_REQUEST_ACTIONS.rejected: {
      const base = `declined to release ${report} to ${sponsor}`;
      const reason = String(item?.reason ?? "").trim();
      return reason ? `${base}: "${reason}".` : `${base}.`;
    }
    default:
      return String(item?.action ?? "updated this library request.");
  }
}

export const describeLibraryRequestActivity = (item, fallbacks = {}) => {
  const actor = formatLibraryRequestActivityActorLabel(item, fallbacks);
  const actionOn = item?.action_on;

  return {
    actor,
    message: formatLibraryRequestActivityMessage(item),
    when: actionOn,
    whenLabel: formatLibraryRequestActivityDate(actionOn),
  };
};

export function sortLibraryRequestActivityNewestFirst(activity) {
  return [...(activity ?? [])].sort(
    (a, b) => new Date(b?.action_on ?? 0) - new Date(a?.action_on ?? 0)
  );
}
